// frontend/src/components/Financeiro/FinanceiroDashboard.js
// =============================================
// DASHBOARD FINANCEIRO
// ✅ KPIs consolidados (receitas, despesas, lucro, saldo)
// ✅ Evolução mensal, top categorias, top fornecedores
// ✅ PERSONALIZAÇÃO DE MOEDA E FORMATAÇÃO DINÂMICA
// 🔥 configuracao (moeda+formatacao) + empresa vêm diretamente
//    da resposta do endpoint /api/financeiro/completo — sem chamadas
//    duplicadas a /api/empresas/minha/atual
// 🔥 BLOCO 5 — COMPRAS A FORNECEDOR integradas:
//    - KPIs de Compras (total, pago, a pagar, IVA dedutível)
//    - Bloco `dados.compras` dedicado
//    - IVA Dedutível decomposto: Despesas + Stock + Compras
//    - Evolução inclui linha "Compras"
//    - Alerta de compras pendentes há +30 dias
//    - Top Fornecedores agora vem de CompraFornecedor
//    - Nova secção com tabela-resumo de Compras
// =============================================
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
    AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { Icon } from './SharedComponents';

const API = '/api' || 'http://localhost:3000';

const mesesOpcoes = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

// 🔥 CONFIGURAÇÃO PADRÃO (fallback)
const CONFIG_PADRAO = {
    moeda: {
        simbolo: 'MT',
        codigo: 'MZN',
        casasDecimais: 2,
        separadorDecimal: ',',
        separadorMilhar: '.',
        posicaoSimbolo: 'depois',
        nome: 'Metical'
    },
    formatacao: {
        data: { formato: 'DD/MM/YYYY', separador: '/', incluirHora: false },
        numero: { separadorDecimal: ',', separadorMilhar: '.', casasDecimais: 2 }
    }
};

// =============================================
// TOOLTIP CUSTOMIZADA
// =============================================
function CustomTooltip({ active, payload, label, formatarMoeda }) {
    if (!active || !payload || !payload.length) return null;
    return (
        <div className="fin-tooltip">
            {label && <div className="fin-tooltip-label">{label}</div>}
            {payload.map((entry, i) => (
                <div key={i} className="fin-tooltip-item">
                    <span className="fin-tooltip-dot" style={{ background: entry.color }} />
                    <span className="fin-tooltip-name">{entry.name}:</span>
                    <span className="fin-tooltip-value">{formatarMoeda(entry.value)}</span>
                </div>
            ))}
        </div>
    );
}

// =============================================
// KPI CARD
// =============================================
function KpiCard({ titulo, valor, sub, icone, cor, semDados }) {
    return (
        <div className="fin-kpi-card" style={{ borderColor: cor }}>
            <div className="fin-kpi-icon" style={{ background: cor + '20', color: cor }}>
                <Icon name={icone} size={22} color={cor} />
            </div>
            <div className="fin-kpi-body">
                <span className="fin-kpi-titulo">{titulo}</span>
                <span className="fin-kpi-valor" style={{ opacity: semDados ? 0.45 : 1 }}>
                    {semDados ? '—' : valor}
                </span>
                {sub && !semDados && <span className="fin-kpi-sub">{sub}</span>}
                {semDados && <span className="fin-kpi-sub">sem dados</span>}
            </div>
        </div>
    );
}

export default function FinanceiroDashboard() {
    const [periodo, setPeriodo] = useState('mes');
    const [ano, setAno] = useState(new Date().getFullYear());
    const [mes, setMes] = useState(new Date().getMonth());
    const [dados, setDados] = useState(null);
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState(null);

    // 🔥 Estado para lista de fornecedores (para cruzamento)
    const [fornecedoresLista, setFornecedoresLista] = useState([]);

    // 🔥 Config da empresa — começa com padrão
    const [empresaConfig, setEmpresaConfig] = useState(CONFIG_PADRAO);

    const token = localStorage.getItem('token');
    const headers = useMemo(
        () => ({ Authorization: `Bearer ${token}` }),
        [token]
    );

    // =============================================
    // 🔥 FORMATAR MOEDA (DINÂMICA)
    // =============================================
    const formatarMoeda = useMemo(() => {
        return (valor) => {
            const cfg = empresaConfig.moeda || CONFIG_PADRAO.moeda;
            const sepDec = cfg.separadorDecimal || ',';
            const sepMil = cfg.separadorMilhar || '.';
            const casasDec = cfg.casasDecimais ?? 2;
            const simbolo = cfg.simbolo || 'MT';

            if (valor === undefined || valor === null || isNaN(valor)) {
                const zeros = '0'.repeat(casasDec);
                return `0${sepDec}${zeros} ${simbolo}`;
            }

            let valorFormatado = Number(valor).toFixed(casasDec);
            valorFormatado = valorFormatado.replace('.', sepDec);

            const partes = valorFormatado.split(sepDec);
            partes[0] = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, sepMil);
            valorFormatado = partes.join(sepDec);

            return `${valorFormatado} ${simbolo}`;
        };
    }, [empresaConfig.moeda]);

    // =============================================
    // 🔥 FORMATAR NÚMEROS (DINÂMICA)
    // =============================================
    const formatarNumero = useMemo(() => {
        return (valor) => {
            const cfg = empresaConfig.formatacao?.numero || empresaConfig.moeda || CONFIG_PADRAO.formatacao.numero;
            const sepDec = cfg.separadorDecimal || ',';
            const sepMil = cfg.separadorMilhar || '.';
            const casasDec = cfg.casasDecimais !== undefined ? cfg.casasDecimais : 0;

            if (valor === undefined || valor === null || isNaN(valor)) {
                return '0';
            }

            let valorFormatado = Number(valor).toFixed(casasDec);
            valorFormatado = valorFormatado.replace('.', sepDec);

            const partes = valorFormatado.split(sepDec);
            partes[0] = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, sepMil);
            valorFormatado = partes.join(sepDec);

            return valorFormatado;
        };
    }, [empresaConfig.formatacao, empresaConfig.moeda]);

    // =============================================
    // 🔥 FORMATAR DATA (DINÂMICA)
    // =============================================
    const formatarData = useCallback((data) => {
        if (!data) return '-';
        const d = new Date(data);
        if (isNaN(d.getTime())) return '-';

        const cfg = empresaConfig.formatacao?.data || CONFIG_PADRAO.formatacao.data;
        let formato = cfg.formato || 'DD/MM/YYYY';
        const dia = String(d.getDate()).padStart(2, '0');
        const mesNum = String(d.getMonth() + 1).padStart(2, '0');
        const anoNum = d.getFullYear();

        formato = formato.replace('DD', dia);
        formato = formato.replace('MM', mesNum);
        formato = formato.replace('YYYY', anoNum);
        formato = formato.replace('YY', String(anoNum).slice(-2));

        if (cfg.incluirHora) {
            const horas = String(d.getHours()).padStart(2, '0');
            const minutos = String(d.getMinutes()).padStart(2, '0');
            return `${formato} ${horas}:${minutos}`;
        }

        return formato;
    }, [empresaConfig.formatacao]);

    // =============================================
    // 🔥 APLICAR CONFIGURAÇÃO DA EMPRESA (helper)
    // =============================================
    const aplicarConfiguracao = useCallback((resData) => {
        if (!resData) return;
        const cfg = resData.configuracao;
        const emp = resData.empresa;

        if (cfg || emp) {
            setEmpresaConfig(prev => ({
                ...prev,
                moeda: cfg?.moeda || emp?.moeda || prev.moeda,
                formatacao: cfg?.formatacao || emp?.formatacao || prev.formatacao
            }));
        }
    }, []);

    // =============================================
    // CARREGAR DADOS (fornecedores + dashboard)
    // =============================================
    const carregar = useCallback(async () => {
        setLoading(true);
        setErro(null);
        try {
            // 1. Buscar lista completa de fornecedores (para cruzar dados)
            try {
                const fornRes = await axios.get(`${API}/fornecedores`, { headers });
                if (fornRes.data) {
                    const lista = Array.isArray(fornRes.data) ? fornRes.data : fornRes.data.data || [];
                    setFornecedoresLista(lista);
                }
            } catch (e) {
                console.warn('⚠️ Não foi possível carregar a lista de fornecedores para o dashboard.', e);
            }

            // 2. Buscar dados do dashboard financeiro (já traz config + empresa)
            const res = await axios.get(`${API}/financeiro/completo`, {
                params: { periodo, ano, mes },
                headers
            });
            if (res.data.success) {
                setDados(res.data);
                // 🔥 Aplicar config (moeda + formatacao) da resposta
                aplicarConfiguracao(res.data);
            }
        } catch (e) {
            setErro(e.response?.data?.message || e.message);
        } finally {
            setLoading(false);
        }
    }, [periodo, ano, mes, headers, aplicarConfiguracao]);

    useEffect(() => { carregar(); }, [carregar]);

    if (loading && !dados) return <div className="fin-loading"><div className="fin-spinner" />A carregar...</div>;
    if (erro && !dados) return <div className="fin-erro"><Icon name="error" size={40} color="var(--fin-danger)" /><p>{erro}</p><button onClick={carregar}>Tentar novamente</button></div>;

    const k = dados?.kpis || {};
    const evolucao = dados?.evolucao || [];
    const despCat = dados?.despesasPorCategoria || [];

    // 🔥 BLOCO 5: bloco de compras a fornecedor
    const compras = dados?.compras || {
        totalCompras: 0,
        totalPagoCompras: 0,
        totalAPagar: 0,
        numeroCompras: 0,
        baseTributavel: 0,
        ivaDedutivel: 0
    };

    // 🔥 CRUZAMENTO DE DADOS DOS FORNECEDORES
    const topFornecedoresRaw = dados?.topFornecedores || [];
    const topFornecedores = topFornecedoresRaw.map(item => {
        if (item.nome) return item;

        const fornecedorEncontrado = fornecedoresLista.find(
            f => f._id === item._id || f._id === item.fornecedorId
        );
        if (fornecedorEncontrado) {
            return { ...item, nome: fornecedorEncontrado.nome };
        }

        return { ...item, nome: item._id || 'Fornecedor Desconhecido' };
    });

    const topCategorias = dados?.topCategorias || [];
    const alertas = dados?.alertas || {};

    const temDados = k.totalReceitas > 0 || k.totalDespesas > 0;
    const temEvolucao = evolucao.some(e => e.receita > 0 || e.despesa > 0 || e.compras > 0);
    const temCompras = (compras.numeroCompras || 0) > 0;

    const saldo = (Number(k.totalReceitas) || 0) - (Number(k.totalDespesas) || 0);

    return (
        <div className="fin-dash">
            {/* ===== HEADER ===== */}
            <header className="fin-dash-header">
                <div className="fin-dash-title">
                    <Icon name="analytics" size={26} color="var(--fin-primary)" />
                    <div>
                        <h2>Dashboard Financeiro</h2>
                        <p>Receitas • Despesas • Compras • Lucro • Cobranças</p>
                    </div>
                </div>
                <div className="fin-dash-filtros">
                    <select value={periodo} onChange={e => setPeriodo(e.target.value)}>
                        <option value="mes">Mês</option>
                        <option value="trimestre">Trimestre</option>
                        <option value="ano">Ano</option>
                    </select>
                    {periodo === 'mes' && (
                        <select value={mes} onChange={e => setMes(parseInt(e.target.value))}>
                            {mesesOpcoes.map((m, i) => <option key={i} value={i}>{m}</option>)}
                        </select>
                    )}
                    <select value={ano} onChange={e => setAno(parseInt(e.target.value))}>
                        {[2023, 2024, 2025, 2026, 2027].map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                    <button className="fin-btn-refresh" onClick={carregar} title="Atualizar">
                        <Icon name="refresh" size={16} color="#fff" />
                    </button>
                </div>
            </header>

            {/* ===== ALERTAS ===== */}
            {(alertas.despesasVencidas > 0 || alertas.faturasVencidas > 0 || alertas.despesasAVencer > 0 || alertas.comprasPendentes > 0) && (
                <section className="fin-alertas">
                    {alertas.faturasVencidas > 0 && (
                        <div className="fin-alerta alerta-danger">
                            <Icon name="receipt_long" size={16} color="var(--fin-danger)" />
                            <div>
                                <strong>{alertas.faturasVencidas} fatura(s) vencida(s)</strong>
                                <span>Requer cobrança imediata</span>
                            </div>
                        </div>
                    )}
                    {alertas.despesasVencidas > 0 && (
                        <div className="fin-alerta alerta-danger">
                            <Icon name="trending_down" size={16} color="var(--fin-danger)" />
                            <div>
                                <strong>{alertas.despesasVencidas} despesa(s) vencida(s)</strong>
                                <span>Pagar aos fornecedores</span>
                            </div>
                        </div>
                    )}
                    {alertas.despesasAVencer > 0 && (
                        <div className="fin-alerta alerta-warning">
                            <Icon name="clock" size={16} color="var(--fin-warning)" />
                            <div>
                                <strong>{alertas.despesasAVencer} despesa(s) a vencer em 7 dias</strong>
                                <span>Prepare os pagamentos</span>
                            </div>
                        </div>
                    )}
                    {/* 🔥 BLOCO 5: alerta de compras pendentes há +30 dias */}
                    {alertas.comprasPendentes > 0 && (
                        <div className="fin-alerta alerta-warning">
                            <Icon name="local_shipping" size={16} color="var(--fin-warning)" />
                            <div>
                                <strong>{alertas.comprasPendentes} compra(s) pendente(s) há +30 dias</strong>
                                <span>Regularizar pagamento a fornecedores</span>
                            </div>
                        </div>
                    )}
                </section>
            )}

            {/* ===== KPIs — VENDAS ===== */}
            <section className="fin-dash-kpis">
                <KpiCard
                    titulo="Total Receitas"
                    valor={formatarMoeda(k.totalReceitas)}
                    sub={`${formatarNumero(k.numeroFaturas)} faturas`}
                    icone="trending_up"
                    cor="#3b82f6"
                    semDados={!k.numeroFaturas}
                />
                <KpiCard
                    titulo="Total Despesas"
                    valor={formatarMoeda(k.totalDespesas)}
                    sub={`${formatarNumero(k.numeroDespesas)} despesas`}
                    icone="trending_down"
                    cor="#ef4444"
                    semDados={!k.numeroDespesas}
                />
                <KpiCard
                    titulo="Lucro Líquido"
                    valor={formatarMoeda(k.lucroLiquido)}
                    sub={`margem ${k.margemLucro || 0}%`}
                    icone="show_chart"
                    cor={k.lucroLiquido >= 0 ? '#10b981' : '#dc2626'}
                    semDados={!temDados}
                />
                <KpiCard
                    titulo="Saldo do Período"
                    valor={formatarMoeda(saldo)}
                    sub={saldo >= 0 ? 'saldo positivo' : 'saldo negativo'}
                    icone="account_balance"
                    cor={saldo >= 0 ? '#8b5cf6' : '#dc2626'}
                    semDados={!temDados}
                />
                <KpiCard
                    titulo="A Receber"
                    valor={formatarMoeda(k.aReceber)}
                    sub="saldo em aberto"
                    icone="attach_money"
                    cor="#f59e0b"
                    semDados={!k.numeroFaturas}
                />
                <KpiCard
                    titulo="A Pagar"
                    valor={formatarMoeda(k.aPagar)}
                    // 🔥 BLOCO 5: discriminar despesas + compras
                    sub={`${formatarMoeda(k.aPagarDespesas || 0)} desp. + ${formatarMoeda(k.aPagarCompras || 0)} compras`}
                    icone="money"
                    cor="#06b6d4"
                    semDados={!k.numeroDespesas && !k.numeroCompras}
                />
            </section>

            {/* ===== 🔥 BLOCO 5: KPIs — COMPRAS A FORNECEDOR ===== */}
            <section className="fin-dash-kpis">
                <KpiCard
                    titulo="Total Compras"
                    valor={formatarMoeda(compras.totalCompras)}
                    sub={`${formatarNumero(compras.numeroCompras)} compras`}
                    icone="local_shipping"
                    cor="#0ea5e9"
                    semDados={!compras.numeroCompras}
                />
                <KpiCard
                    titulo="Pago a Fornecedores"
                    valor={formatarMoeda(compras.totalPagoCompras)}
                    sub="total liquidado"
                    icone="arrow_upward"
                    cor="#14b8a6"
                    semDados={!compras.numeroCompras}
                />
                <KpiCard
                    titulo="A Pagar a Fornecedores"
                    valor={formatarMoeda(compras.totalAPagar)}
                    sub="saldo em aberto"
                    icone="arrow_downward"
                    cor="#f97316"
                    semDados={!compras.numeroCompras}
                />
                <KpiCard
                    titulo="IVA Dedutível — Compras"
                    valor={formatarMoeda(k.ivaDedutivelCompras)}
                    sub="IVA suportado"
                    icone="receipt"
                    cor="#a855f7"
                    semDados={!compras.numeroCompras}
                />
                <KpiCard
                    titulo="IVA Dedutível — Stock"
                    valor={formatarMoeda(k.ivaDedutivelStock)}
                    sub="via entradas de produto"
                    icone="inventory_2"
                    cor="#059669"
                    semDados={!k.ivaDedutivelStock}
                />
                <KpiCard
                    titulo="IVA a Entregar"
                    valor={formatarMoeda(k.ivaAEntregar)}
                    sub={`IVA Liq. ${formatarMoeda(k.ivaLiquidado)} − Ded. ${formatarMoeda(k.ivaDedutivel)}`}
                    icone="account_balance_wallet"
                    cor="#dc2626"
                    semDados={!k.numeroFaturas && !compras.numeroCompras}
                />
            </section>

            {/* ===== EVOLUÇÃO ===== */}
            <section className="fin-dash-grid-2">
                <div className="fin-card">
                    <h3>
                        <Icon name="show_chart" size={16} color="var(--fin-primary)" />
                        Receita vs Despesa vs Compras vs Lucro ({ano})
                    </h3>
                    {!temEvolucao ? (
                        <p className="fin-vazio">Sem dados para {ano}</p>
                    ) : (
                        <ResponsiveContainer width="100%" height={280}>
                            <AreaChart data={evolucao} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
                                <defs>
                                    <linearGradient id="gradReceita" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.7} />
                                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.05} />
                                    </linearGradient>
                                    <linearGradient id="gradDespesa" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#ef4444" stopOpacity={0.7} />
                                        <stop offset="95%" stopColor="#ef4444" stopOpacity={0.05} />
                                    </linearGradient>
                                    {/* 🔥 BLOCO 5: gradiente de compras */}
                                    <linearGradient id="gradCompras" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#f97316" stopOpacity={0.7} />
                                        <stop offset="95%" stopColor="#f97316" stopOpacity={0.05} />
                                    </linearGradient>
                                    <linearGradient id="gradLucro" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.7} />
                                        <stop offset="95%" stopColor="#10b981" stopOpacity={0.05} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                                <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} width={45} />
                                <Tooltip content={<CustomTooltip formatarMoeda={formatarMoeda} />} />
                                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }} />
                                <Area type="monotone" dataKey="receita" stroke="#3b82f6" fill="url(#gradReceita)" name="Receita" strokeWidth={2} />
                                <Area type="monotone" dataKey="despesa" stroke="#ef4444" fill="url(#gradDespesa)" name="Despesa" strokeWidth={2} />
                                {/* 🔥 BLOCO 5: linha de compras */}
                                <Area type="monotone" dataKey="compras" stroke="#f97316" fill="url(#gradCompras)" name="Compras" strokeWidth={2} />
                                <Area type="monotone" dataKey="lucro" stroke="#10b981" fill="url(#gradLucro)" name="Lucro" strokeWidth={2} />
                            </AreaChart>
                        </ResponsiveContainer>
                    )}
                </div>

                <div className="fin-card">
                    <h3>
                        <Icon name="category" size={16} color="var(--fin-primary)" />
                        Despesas por Categoria
                    </h3>
                    {despCat.length === 0 ? (
                        <p className="fin-vazio">Sem despesas no período</p>
                    ) : (
                        <ResponsiveContainer width="100%" height={280}>
                            <PieChart>
                                <Pie
                                    data={despCat}
                                    dataKey="valor"
                                    nameKey="categoria"
                                    cx="50%" cy="45%"
                                    outerRadius="70%"
                                    innerRadius="40%"
                                    paddingAngle={2}
                                    label={({ percent }) => {
                                        if (percent < 0.05) return null;
                                        return `${(percent * 100).toFixed(0)}%`;
                                    }}
                                >
                                    {despCat.map((d, i) => <Cell key={i} fill={d.cor} />)}
                                </Pie>
                                <Tooltip formatter={(v) => formatarMoeda(v)} />
                                <Legend verticalAlign="bottom" height={36} iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }} />
                            </PieChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </section>

            {/* ===== TOP CATEGORIAS + TOP FORNECEDORES ===== */}
            <section className="fin-dash-grid-2">
                <div className="fin-card">
                    <h3>
                        <Icon name="category" size={16} color="var(--fin-primary)" />
                        Top 5 Categorias de Despesa
                    </h3>
                    {topCategorias.length === 0 ? (
                        <p className="fin-vazio">Sem dados</p>
                    ) : (
                        <div className="fin-top-list">
                            {topCategorias.map((c, i) => (
                                <div key={i} className="fin-top-item">
                                    <span className="fin-top-pos">{i + 1}</span>
                                    <span className="fin-top-dot" style={{ background: c.cor }} />
                                    <span className="fin-top-nome">{c.categoria}</span>
                                    <span className="fin-top-valor">{formatarMoeda(c.valor)}</span>
                                    <span className="fin-top-count">{formatarNumero(c.count)}x</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="fin-card">
                    <h3>
                        <Icon name="storefront" size={16} color="var(--fin-primary)" />
                        Top 5 Fornecedores (Compras)
                    </h3>
                    {topFornecedores.length === 0 ? (
                        <p className="fin-vazio">Sem compras no período</p>
                    ) : (
                        <div className="fin-top-list">
                            {topFornecedores.map((f, i) => (
                                <div key={i} className="fin-top-item">
                                    <span className="fin-top-pos">{i + 1}</span>
                                    <span className="fin-top-nome">{f.nome}</span>
                                    <span className="fin-top-valor">{formatarMoeda(f.total)}</span>
                                    <span className="fin-top-count">{formatarNumero(f.count)}x</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* ===== 🔥 BLOCO 5: PAINEL DE COMPRAS A FORNECEDOR ===== */}
            <section className="fin-card">
                <h3>
                    <Icon name="local_shipping" size={16} color="var(--fin-primary)" />
                    Compras a Fornecedor ({ano})
                </h3>
                {!temCompras ? (
                    <p className="fin-vazio">Sem compras registadas para o período</p>
                ) : (
                    <div className="fin-compras-resumo">
                        <div className="fin-compras-item">
                            <span>Total de Compras</span>
                            <strong>{formatarMoeda(compras.totalCompras)}</strong>
                            <small>{formatarNumero(compras.numeroCompras)} compras</small>
                        </div>
                        <div className="fin-compras-item">
                            <span>Pago a Fornecedores</span>
                            <strong>{formatarMoeda(compras.totalPagoCompras)}</strong>
                            <small>total liquidado</small>
                        </div>
                        <div className="fin-compras-item destaque-perigo">
                            <span>A Pagar a Fornecedores</span>
                            <strong>{formatarMoeda(compras.totalAPagar)}</strong>
                            <small>saldo em aberto</small>
                        </div>
                        <div className="fin-compras-item destaque-roxo">
                            <span>IVA Dedutível (Compras)</span>
                            <strong>{formatarMoeda(compras.ivaDedutivel)}</strong>
                            <small>IVA suportado</small>
                        </div>
                        <div className="fin-compras-item">
                            <span>Base Tributável</span>
                            <strong>{formatarMoeda(compras.baseTributavel)}</strong>
                            <small>subtotal</small>
                        </div>
                    </div>
                )}
            </section>

            {/* 🔥 Estilos auxiliares para este componente */}
            <style>{`
                .fin-compras-resumo {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
                    gap: 0.75rem;
                    margin-top: 0.5rem;
                }
                .fin-compras-item {
                    display: flex;
                    flex-direction: column;
                    gap: 0.25rem;
                    padding: 0.85rem 1rem;
                    background: var(--fin-bg-header, #f8fafc);
                    border-radius: 10px;
                    border: 2px solid transparent;
                    transition: all 0.25s ease;
                }
                .fin-compras-item:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
                }
                .fin-compras-item span {
                    font-size: 0.55rem;
                    color: var(--fin-muted, #94a3b8);
                    text-transform: uppercase;
                    letter-spacing: 0.4px;
                    font-weight: 700;
                }
                .fin-compras-item strong {
                    font-size: 1rem;
                    font-weight: 800;
                    color: var(--fin-dark, #1a1a2e);
                }
                .fin-compras-item small {
                    font-size: 0.6rem;
                    color: var(--fin-info-dark, #6b7280);
                    font-weight: 500;
                }
                .fin-compras-item.destaque-perigo {
                    background: rgba(220, 38, 38, 0.06);
                    border-color: #dc2626;
                }
                .fin-compras-item.destaque-perigo strong {
                    color: #dc2626;
                }
                .fin-compras-item.destaque-roxo {
                    background: rgba(168, 85, 247, 0.06);
                    border-color: #a855f7;
                }
                .fin-compras-item.destaque-roxo strong {
                    color: #a855f7;
                }
                .dark-mode-variables .fin-compras-item {
                    background: rgba(255, 255, 255, 0.04);
                }
                .dark-mode-variables .fin-compras-item strong {
                    color: #fff;
                }
                .dark-mode-variables .fin-compras-item span {
                    color: rgba(255, 255, 255, 0.5);
                }
                .dark-mode-variables .fin-compras-item small {
                    color: rgba(255, 255, 255, 0.4);
                }
                .dark-mode-variables .fin-compras-item.destaque-perigo {
                    background: rgba(248, 113, 113, 0.12);
                }
                .dark-mode-variables .fin-compras-item.destaque-perigo strong {
                    color: #f87171;
                }
                .dark-mode-variables .fin-compras-item.destaque-roxo {
                    background: rgba(167, 139, 250, 0.12);
                }
                .dark-mode-variables .fin-compras-item.destaque-roxo strong {
                    color: #c4b5fd;
                }
                /* Espaço entre linhas de KPIs */
                .fin-dash-kpis + .fin-dash-kpis {
                    margin-top: 0.75rem;
                }
            `}</style>
        </div>
    );
}